package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.first
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.saves.SaveOutcome
import com.evoapps.evoshift.saves.fold
import com.evoapps.evoshift.saves.onFailure
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cost_rule_added
import evoshift.composeapp.generated.resources.error_save_failed
import evoshift.composeapp.generated.resources.error_not_found
import evoshift.composeapp.generated.resources.group_settings_own_done
import evoshift.composeapp.generated.resources.group_settings_reset_done
import evoshift.composeapp.generated.resources.group_settings_saved_all
import evoshift.composeapp.generated.resources.group_settings_saved_some_own
import evoshift.composeapp.generated.resources.request_type_contracts_none
import evoshift.composeapp.generated.resources.settings_financials_updated
import evoshift.composeapp.generated.resources.settings_labour_budget_saved
import evoshift.composeapp.generated.resources.settings_location_updated
import evoshift.composeapp.generated.resources.settings_name_updated
import evoshift.composeapp.generated.resources.settings_policy_updated
import evoshift.composeapp.generated.resources.settings_saved
import evoshift.composeapp.generated.resources.settings_updated
import evoshift.composeapp.generated.resources.settings_workplace_removed
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.CostFrequency
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.model.SwapPolicy
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.settings.CreateRecurringCostRuleUseCase
import com.evoapps.evoshift.domain.usecase.settings.DeleteWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceFinancialsUseCase
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceLocationUseCase
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceNameUseCase
import com.evoapps.evoshift.domain.usecase.settings.UpdateWorkplaceSettingsUseCase
import com.evoapps.evoshift.domain.util.normalizeTime
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SettingsUiState(
    val isLoading: Boolean = false,
    /** Removing the workplace is in flight: the confirm dialog shows progress and stays open. */
    val isSaving: Boolean = false,
    /** The workplace was removed: the screen leaves. */
    val isRemoved: Boolean = false,
    val workplace: Workplace? = null,
    val workplaceFinancials: WorkplaceFinancials? = null,
    /** SH-115: the labour budget, for those who may change the financial settings; null otherwise. */
    val labourBudget: com.evoapps.evoshift.domain.budget.LabourBudget? = null,
    /** What the person may do at this workplace: which sections show and which edits are allowed. */
    val capabilities: Set<Capability> = emptySet(),
    val error: UiText? = null,
    val successMessage: UiText? = null,
    /** SH-223: the shared places to work from (the managers rename and remove them here). */
    val places: List<com.evoapps.evoshift.domain.model.RequestPlace> = emptyList(),
    /**
     * The owner's own contract types (owners/{uid}/contract_types, live), besides the built-in
     * contracts: the request types' settings tick them. Empty for whoever may not read them.
     */
    val customContractTypes: List<com.evoapps.evoshift.domain.model.ContractType> = emptyList(),
    /** The request type whose settings are open (a sheet on phones, a dialog on wide screens). */
    val requestTypeDraft: RequestTypeDraft? = null,
    /**
     * SH-263: the request types' dialog is open with this working copy of the request settings
     * (the ticked types, their contracts and numbers); nothing is saved before its Save.
     */
    val requestTypesDraft: com.evoapps.evoshift.domain.model.RequestSettings? = null,
    /**
     * SH-239: the "Made with EVoShift" footer may be hidden: the owner pays for a plan (not the
     * trial, not unknown, not ended). Otherwise the switch stays on and says why.
     */
    val footerCanBeHidden: Boolean = false,
    /**
     * SH-269: the workplace's link to its group (the group's values, the sections that follow it);
     * null while it is in no group. Shown as "Group settings (name)" / "Own settings" per section
     * while the group has more than one workplace.
     */
    val groupLink: com.evoapps.evoshift.domain.model.GroupSettingsLink? = null,
    /** SH-269: set: the screen edits this group's values (the owner), not a workplace's. */
    val group: GroupSettingsTarget? = null,
    /** SH-269 (group mode): per section, the group's workplaces that follow it and those with their own. */
    val groupFollowing: Map<com.evoapps.evoshift.domain.model.SettingsSection, List<String>> = emptyMap(),
    val groupOwn: Map<com.evoapps.evoshift.domain.model.SettingsSection, List<String>> = emptyMap()
) {
    /** SH-269: the group's values are edited here (no workplace-only sections). */
    val isGroupMode: Boolean get() = group != null

    /** SH-269: where a workplace's section takes its values from; null: no shared group to follow. */
    fun sourceOf(section: com.evoapps.evoshift.domain.model.SettingsSection): com.evoapps.evoshift.domain.model.SectionSource? {
        val link = groupLink?.takeIf { it.isShared && group == null } ?: return null
        return if (link.follows(section)) com.evoapps.evoshift.domain.model.SectionSource.GROUP else com.evoapps.evoshift.domain.model.SectionSource.OWN
    }

    /** SH-269: the group's values when the workplace follows the group in [section] (the greyed placeholders). */
    fun groupValuesIfFollowed(section: com.evoapps.evoshift.domain.model.SettingsSection): com.evoapps.evoshift.domain.model.SettingsValues? =
        groupLink?.group?.takeIf { sourceOf(section) == com.evoapps.evoshift.domain.model.SectionSource.GROUP }
}

/** SH-269: the group the settings screen edits: its name as shown and its workplaces (id to name). */
data class GroupSettingsTarget(
    val groupId: String,
    val name: String,
    val usesDefaultName: Boolean,
    val workplaces: Map<String, String>
)

/**
 * One request type's settings being changed: its [rule] (on or off, the contracts) and the
 * law's [numbers] it has (blood donation days, the yearly days or hours), saved together.
 */
data class RequestTypeDraft(
    val typeName: String,
    val rule: com.evoapps.evoshift.domain.model.RequestTypeRule,
    val numbers: com.evoapps.evoshift.domain.model.RequestSettings,
    /** Save was tapped with no contract ticked. */
    val saveRefused: Boolean = false
) {
    /** Switched on but offered on no contract: it cannot be saved. */
    val missingContract: Boolean get() = rule.enabled && !rule.hasAnyContract
}

/** SH-263: the request types switched on, in the settings' order (by group, then within it). */
fun com.evoapps.evoshift.domain.model.RequestSettings.enabledTypeNames(): List<String> =
    com.evoapps.evoshift.domain.requests.RequestSettingsGroup.entries.flatMap { it.typeNames }.filter { ruleOf(it).enabled }

/** SH-263: the switched-on types offered on no contract (they cannot be saved). */
fun com.evoapps.evoshift.domain.model.RequestSettings.typesMissingContract(): List<String> =
    enabledTypeNames().filter { !ruleOf(it).hasAnyContract }

sealed interface SettingsEvent {
    // Ładowanie
    data class LoadSettings(val workplaceId: String) : SettingsEvent

    // Proste pola
    data class UpdateGeneralSettings(
        val timezone: String,
        val currency: String,
        val firstDayOfWeek: Int
    ) : SettingsEvent

    // Nowe zdarzenie
    data class UpdateWorkplaceName(val newName: String) : SettingsEvent

    // Zdarzenie aktualizujące wszystkie podstawowe ustawienia naraz (w tym nazwę)
    data class UpdateBasicSettings(
        val name: String,
        val timezone: String,
        val currency: String,
        val firstDayOfWeek: Int
    ) : SettingsEvent

    // Listy Stringów (Role, Skills)
    data class AddRole(val role: String) : SettingsEvent
    data class RemoveRole(val role: String) : SettingsEvent
    data class AddSkill(val skill: String) : SettingsEvent
    data class RemoveSkill(val skill: String) : SettingsEvent
    /** SH-104: whether people in this position lead shifts. */
    data class SetLeadRole(val role: String, val leads: Boolean) : SettingsEvent
    /** SH-104: whether people with this skill lead shifts. */
    data class SetLeadSkill(val skill: String, val leads: Boolean) : SettingsEvent

    // Polityka
    data class ChangeSwapPolicy(val policy: SwapPolicy) : SettingsEvent

    // Cele sprzedażowe (Sales Targets)
    data class UpdateSalesTarget(val dayKey: String, val target: Double) : SettingsEvent
    data class UpdateSalesTargets(val targets: Map<String, Double>) : SettingsEvent

    // Sloty dostępności
    data class AddAvailabilitySlot(val slot: AvailabilitySlot) : SettingsEvent
    data class RemoveAvailabilitySlot(val slotId: String) : SettingsEvent

    data class AddRecurringCost(val name: String, val amount: Double) : SettingsEvent

    data class UpdateLocation(val address: String, val location: GeoLocation?) : SettingsEvent
    data class UpdateFinancials(val financials: WorkplaceFinancials) : SettingsEvent
    /** SH-115: the labour budget ([com.evoapps.evoshift.domain.budget.LabourBudget.OFF] switches it off). */
    data class UpdateLabourBudget(val budget: com.evoapps.evoshift.domain.budget.LabourBudget) : SettingsEvent
    data class UpdatePayroll(val payroll: WorkplacePayrollSettings) : SettingsEvent
    /** Minutes without a clock-in before managers are told (SH-73); 0 = off. */
    data class UpdateNoShowAlert(val minutes: Int) : SettingsEvent
    /** SH-121: minutes after a shift with its closing checklist not done before the reminder; 0 = off. */
    data class UpdateChecklistAlert(val minutes: Int) : SettingsEvent
    /** SH-47: refuse (true) or only warn about (false) assigning someone with an expired exam or training. */
    data class UpdateBlockExpiredStaffChecks(val block: Boolean) : SettingsEvent
    /** SH-133: the 11 h and 35 h rest for every contract (true) or employment contracts only. */
    data class UpdateRestRulesForAllContracts(val all: Boolean) : SettingsEvent
    /** SH-226: weather for the workplace's location (fetched by the server) on or off. */
    data class UpdateWeatherEnabled(val enabled: Boolean) : SettingsEvent
    /** SH-257: the team enters their own hours (any day not confirmed and not closed). */
    data class UpdateWorkHours(val settings: com.evoapps.evoshift.domain.model.WorkHoursSettings) : SettingsEvent
    /** SH-255: what employees see of their colleagues' shifts in the roster. */
    data class UpdateRosterVisibility(val visibility: com.evoapps.evoshift.domain.model.RosterVisibility) : SettingsEvent
    /** SH-239: "Made with EVoShift" on the e-mailed rosters; hidden only on a paid plan. */
    data class UpdateEvoShiftFooter(val show: Boolean) : SettingsEvent
    /** SH-258: the printed working-time record's columns, hours and rounding. */
    data class UpdateTimesheetReport(val settings: com.evoapps.evoshift.domain.model.TimesheetReportSettings) : SettingsEvent
    /** SH-227: school breaks and long weekends on the days, and the voivodeship (null: from the address). */
    data class UpdateSpecialDays(val enabled: Boolean, val voivodeship: com.evoapps.evoshift.domain.model.Voivodeship?) : SettingsEvent
    /** Which Sundays and holidays the workplace opens on (SH-55). */
    data class UpdateSundayPolicy(val policy: com.evoapps.evoshift.domain.model.SundayPolicy) : SettingsEvent
    /** SH-106: the clock-in location check (mode and radius). */
    data class UpdateGeofence(val geofence: com.evoapps.evoshift.domain.model.GeofenceSettings) : SettingsEvent
    /** SH-116: paid and unpaid breaks and the break reminder (the finances', as payroll). */
    data class UpdateBreakPolicy(val policy: com.evoapps.evoshift.domain.model.BreakPolicy) : SettingsEvent
    /** SH-134: the clock-in checks (flag thresholds, payable time from the plan, tablet safeguards). */
    data class UpdateClockReview(val settings: com.evoapps.evoshift.domain.model.ClockReviewSettings) : SettingsEvent

    /** SH-223: the request types' numbers (blood donation days, yearly limits). */
    data class UpdateRequestSettings(val settings: com.evoapps.evoshift.domain.model.RequestSettings) : SettingsEvent
    data class RenamePlace(val placeId: String, val name: String) : SettingsEvent
    /** SH-223: one request type switched on or off, or offered on other contracts. */
    data class UpdateRequestType(val typeName: String, val rule: com.evoapps.evoshift.domain.model.RequestTypeRule) : SettingsEvent
    data class RemovePlace(val placeId: String) : SettingsEvent

    /** SH-263: opens the request types' dialog on a copy of the settings in force. */
    data object OpenRequestTypes : SettingsEvent
    /** SH-263: closes the dialog; its changes are dropped. */
    data object CloseRequestTypes : SettingsEvent
    /** SH-263: a type ticked or unticked in the dialog (ticked with no contract: its recommended contracts). */
    data class DraftRequestTypeChecked(val typeName: String, val checked: Boolean) : SettingsEvent
    /** SH-263: the dialog's copy back to the recommended settings (saved only with the dialog). */
    data object DraftRestoreRecommendedRequests : SettingsEvent
    /** SH-263: saves the dialog's copy (refused while a ticked type has no contract). */
    data object SaveRequestTypes : SettingsEvent
    /** Opens a request type's settings (inside the request types' dialog: they change its copy). */
    data class OpenRequestType(val typeName: String) : SettingsEvent
    data object CloseRequestType : SettingsEvent
    data class DraftRequestTypeEnabled(val enabled: Boolean) : SettingsEvent
    /** "Every contract" ticked, or not (then no contract is ticked until one is chosen). */
    data class DraftAllContracts(val all: Boolean) : SettingsEvent
    data class DraftContract(val contractKey: String, val on: Boolean) : SettingsEvent
    data class DraftLimit(val limit: com.evoapps.evoshift.domain.model.RequestLimit, val value: Int) : SettingsEvent
    /** Saves the open request type's settings (refused while it is on with no contract); into the dialog's copy while that is open. */
    data object SaveRequestType : SettingsEvent

    data object RemoveWorkspace : SettingsEvent

    /**
     * SH-269: [section] follows the group (GROUP, "Reset to group": the group's values replace the
     * workplace's) or is the workplace's own (OWN: the values stay, a copy to change).
     */
    data class SetSectionSource(
        val section: com.evoapps.evoshift.domain.model.SettingsSection,
        val source: com.evoapps.evoshift.domain.model.SectionSource
    ) : SettingsEvent
    /** SH-269 (group mode): the group's templates and weekly skeleton become those of [workplaceId]. */
    data class UseTemplatesFrom(val workplaceId: String) : SettingsEvent

    // Obsługa UI
    data object MessageShown : SettingsEvent
}

class WorkplaceSettingsViewModel(
    initialWorkplaceId: String,
    private val updateSettingsUseCase: UpdateWorkplaceSettingsUseCase,
    private val createCostRuleUseCase: CreateRecurringCostRuleUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val updateWorkplaceNameUseCase: UpdateWorkplaceNameUseCase,
    private val updateWorkplaceLocationUseCase: UpdateWorkplaceLocationUseCase,
    private val updateWorkplaceFinancialsUseCase: UpdateWorkplaceFinancialsUseCase,
    private val deleteWorkplaceUseCase: DeleteWorkplaceUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val sessionManager: WorkplaceSessionManager,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-115: the labour budget section; without them it is not shown. */
    private val getLabourBudget: com.evoapps.evoshift.domain.usecase.finance.GetLabourBudgetUseCase? = null,
    private val saveLabourBudget: com.evoapps.evoshift.domain.usecase.finance.SaveLabourBudgetUseCase? = null,
    /** SH-148: the app's saves: a write started here finishes even if the screen is left. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-186: offline, a save unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity(),
    /** SH-223: the shared places; without it the list is not shown. */
    private val workPlaces: com.evoapps.evoshift.domain.usecase.requests.WorkPlacesUseCase? = null,
    /** The owner's contract types for the request types' settings; without it, the built-in contracts only. */
    private val ownerRepository: com.evoapps.evoshift.domain.repository.OwnerRepository? = null,
    /** SH-239: the workplace's plan, for hiding the EVoShift footer (paid plans only); without it, never. */
    private val observeWorkplacePlan: com.evoapps.evoshift.domain.usecase.billing.ObserveWorkplacePlanUseCase? = null,
    private val now: () -> kotlin.time.Instant = { com.evoapps.evoshift.domain.util.AppClock.now() },
    /** SH-269: group settings; without them no section shows where its values come from. */
    private val groupSettings: com.evoapps.evoshift.domain.repository.GroupSettingsRepository? = null,
    private val setSectionInheritance: com.evoapps.evoshift.domain.usecase.owner.SetSectionInheritanceUseCase? = null,
    private val saveGroupSettings: com.evoapps.evoshift.domain.usecase.owner.SaveGroupSettingsUseCase? = null,
    /** SH-269: set: the screen edits this group's values (initialWorkplaceId is then unused). */
    private val groupId: String? = null,
    private val getGroups: com.evoapps.evoshift.domain.usecase.owner.GetWorkplaceGroupsUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(SettingsUiState())
    val uiState: StateFlow<SettingsUiState> = _uiState.asStateFlow()
    private val online get() = connectivity.isOnline

    init {
        if (groupId != null) loadGroup(groupId) else loadWorkplace(initialWorkplaceId)
    }

    /** SH-269 (group mode): the group edited here. */
    private val group: GroupSettingsTarget? get() = _uiState.value.group

    /** The part of the screen an event belongs to; edits outside the person's capabilities are ignored. */
    private val SettingsEvent.gate: Gate?
        get() = when (this) {
            is SettingsEvent.LoadSettings, SettingsEvent.MessageShown -> null
            // Anyone who sees the settings may read a type's settings; the changes need SETTINGS_EDITING.
            is SettingsEvent.OpenRequestType, SettingsEvent.CloseRequestType, SettingsEvent.CloseRequestTypes -> null
            is SettingsEvent.UpdateFinancials, is SettingsEvent.UpdatePayroll, is SettingsEvent.UpdateLabourBudget, is SettingsEvent.UpdateBreakPolicy,
            is SettingsEvent.UpdateClockReview,
            is SettingsEvent.UpdateSalesTarget, is SettingsEvent.UpdateSalesTargets -> Gate.WORKPLACE_FINANCES_EDITING
            is SettingsEvent.AddRecurringCost -> Gate.RECURRING_COSTS
            SettingsEvent.RemoveWorkspace -> Gate.DELETE_WORKPLACE
            // SH-47: the HR side's choice (owners, financial managers), as firestore.rules require.
            is SettingsEvent.UpdateBlockExpiredStaffChecks -> Gate.STAFF_RECORDS_EDITING
            // SH-269: who changes a section's values switches where they come from.
            is SettingsEvent.SetSectionSource -> when {
                section == com.evoapps.evoshift.domain.model.SettingsSection.STAFF_CHECKS -> Gate.STAFF_RECORDS_EDITING
                section.finances -> Gate.WORKPLACE_FINANCES_EDITING
                else -> Gate.SETTINGS_EDITING
            }
            else -> Gate.SETTINGS_EDITING
        }

    fun onEvent(event: SettingsEvent) {
        event.gate?.let { if (!_uiState.value.capabilities.shows(it)) return }
        when (event) {
            is SettingsEvent.LoadSettings -> loadWorkplace(event.workplaceId)
            is SettingsEvent.ChangeSwapPolicy -> updatePolicy(event.policy)
            is SettingsEvent.AddRecurringCost -> addCostRule(event.name, event.amount)
            is SettingsEvent.UpdateLocation -> if (groupId == null) updateLocation(event.address, event.location)
            is SettingsEvent.UpdateFinancials -> updateFinancials(event.financials)
            is SettingsEvent.UpdateLabourBudget -> updateLabourBudget(event.budget)
            is SettingsEvent.UpdatePayroll -> modifySettings { it.copy(payroll = event.payroll) }
            is SettingsEvent.UpdateNoShowAlert -> modifySettings { it.copy(noShowAlertMinutes = event.minutes.coerceIn(0, 120)) }
            is SettingsEvent.UpdateRequestSettings -> modifyRequests { event.settings.clamped() }
            is SettingsEvent.UpdateRequestType -> modifyRequests { it.copy(types = it.types + (event.typeName to event.rule)) }
            SettingsEvent.OpenRequestTypes -> _uiState.value.workplace?.let { wp ->
                _uiState.update { it.copy(requestTypesDraft = wp.settings.requests, requestTypeDraft = null) }
            }
            SettingsEvent.CloseRequestTypes -> _uiState.update { it.copy(requestTypesDraft = null, requestTypeDraft = null) }
            is SettingsEvent.DraftRequestTypeChecked -> updateTypesDraft { checkRequestType(it, event.typeName, event.checked) }
            SettingsEvent.DraftRestoreRecommendedRequests -> updateTypesDraft { com.evoapps.evoshift.domain.model.RequestSettings.RECOMMENDED }
            SettingsEvent.SaveRequestTypes -> saveRequestTypes()
            is SettingsEvent.OpenRequestType -> openRequestType(event.typeName)
            SettingsEvent.CloseRequestType -> _uiState.update { it.copy(requestTypeDraft = null) }
            is SettingsEvent.DraftRequestTypeEnabled -> updateDraft { it.copy(rule = it.rule.copy(enabled = event.enabled)) }
            is SettingsEvent.DraftAllContracts -> updateDraft {
                it.copy(rule = if (event.all) it.rule.copy(contracts = null, contractTypeIds = emptySet()) else it.rule.copy(contracts = emptySet(), contractTypeIds = emptySet()))
            }
            is SettingsEvent.DraftContract -> updateDraft { it.copy(rule = it.rule.withContract(event.contractKey, event.on, customContractIds())) }
            is SettingsEvent.DraftLimit -> updateDraft { it.copy(numbers = it.numbers.with(event.limit, event.value)) }
            SettingsEvent.SaveRequestType -> saveRequestType()
            is SettingsEvent.RenamePlace -> changePlace(event.placeId) { wp -> workPlaces?.rename(wp, event.placeId, event.name) }
            is SettingsEvent.RemovePlace -> changePlace(event.placeId) { wp -> workPlaces?.remove(wp, event.placeId) }
            is SettingsEvent.UpdateChecklistAlert -> modifySettings { it.copy(checklistAlertMinutes = event.minutes.coerceIn(0, 120)) }
            is SettingsEvent.UpdateBlockExpiredStaffChecks -> modifySettings { it.copy(blockExpiredStaffChecks = event.block) }
            is SettingsEvent.UpdateRestRulesForAllContracts -> modifySettings { it.copy(restRulesForAllContracts = event.all) }
            is SettingsEvent.UpdateWeatherEnabled -> modifySettings { it.copy(weatherEnabled = event.enabled) }
            is SettingsEvent.UpdateWorkHours -> modifySettings {
                it.copy(workHours = com.evoapps.evoshift.domain.model.WorkHoursSettings.of(event.settings.selfEntry))
            }
            is SettingsEvent.UpdateTimesheetReport -> modifySettings {
                val t = event.settings
                it.copy(reports = it.reports.copy(timesheet = com.evoapps.evoshift.domain.model.TimesheetReportSettings.of(
                    t.hoursSource, t.roundingMinutes, t.roundingMode, t.showRemarks, t.showManagerSignature, t.weekday
                )))
            }
            is SettingsEvent.UpdateRosterVisibility -> modifySettings { it.copy(rosterVisibility = event.visibility) }
            // SH-239: off only on a paid plan (firestore.rules refuse it otherwise); on always.
            is SettingsEvent.UpdateEvoShiftFooter -> if (event.show || _uiState.value.footerCanBeHidden) {
                modifySettings { it.copy(showEvoShiftFooter = event.show) }
            }
            is SettingsEvent.UpdateSpecialDays -> modifySettings { it.copy(specialDaysEnabled = event.enabled, voivodeship = event.voivodeship) }
            is SettingsEvent.UpdateSundayPolicy -> modifySettings { it.copy(sundayPolicy = event.policy) }
            is SettingsEvent.UpdateGeofence -> modifySettings {
                it.copy(geofence = event.geofence.copy(radiusMeters = com.evoapps.evoshift.domain.model.GeofenceSettings.radiusOrDefault(event.geofence.radiusMeters)))
            }

            is SettingsEvent.UpdateClockReview -> modifySettings {
                val c = event.settings
                it.copy(clockReview = com.evoapps.evoshift.domain.model.ClockReviewSettings.of(
                    c.earlyInMinutes, c.lateOutMinutes, c.maxOverrunMinutes, c.veryShortMinutes, c.countFromPlannedStart,
                    c.kioskQrOnly, c.kioskPhoto, c.sameTabletSeconds, c.flagNoAppActivity
                ))
            }
            is SettingsEvent.UpdateBreakPolicy -> modifySettings {
                it.copy(breakPolicy = com.evoapps.evoshift.domain.model.BreakPolicy.of(
                    event.policy.paidBreak, event.policy.unpaidBreakMinutes, event.policy.reminderAfterHours
                ))
            }

            SettingsEvent.RemoveWorkspace -> if (groupId == null) removeWorkspace()
            is SettingsEvent.SetSectionSource -> setSectionSource(event.section, event.source)
            is SettingsEvent.UseTemplatesFrom -> useTemplatesFrom(event.workplaceId)

            // Reset komunikatów
            SettingsEvent.MessageShown -> _uiState.update {
                it.copy(successMessage = null, error = null)
            }

            is SettingsEvent.UpdateWorkplaceName -> if (groupId == null) updateName(event.newName)

            // --- GENERAL ---
            is SettingsEvent.UpdateGeneralSettings -> {
                modifySettings { current ->
                    current.copy(
                        timezone = event.timezone,
                        currency = event.currency,
                        firstDayOfWeek = event.firstDayOfWeek
                    )
                }
            }

            is SettingsEvent.UpdateBasicSettings -> {
                updateBasicSettings(
                    event.name,
                    event.timezone,
                    event.currency,
                    event.firstDayOfWeek
                )
            }

            // --- ROLES ---
            is SettingsEvent.AddRole -> {
                modifySettings { current ->
                    // Unikamy duplikatów
                    if (event.role !in current.availableRoles) {
                        current.copy(availableRoles = current.availableRoles + event.role)
                    } else current
                }
            }

            is SettingsEvent.RemoveRole -> {
                modifySettings { current ->
                    current.copy(availableRoles = current.availableRoles - event.role, leadRoles = current.leadRoles - event.role)
                }
            }

            // --- SKILLS ---
            is SettingsEvent.AddSkill -> {
                modifySettings { current ->
                    if (event.skill !in current.workplaceSkills) {
                        current.copy(workplaceSkills = current.workplaceSkills + event.skill)
                    } else current
                }
            }

            is SettingsEvent.RemoveSkill -> {
                modifySettings { current ->
                    current.copy(workplaceSkills = current.workplaceSkills - event.skill, leadSkills = current.leadSkills - event.skill)
                }
            }

            // --- SH-104: LEAD MARKS ---
            is SettingsEvent.SetLeadRole -> {
                modifySettings { current ->
                    val others = current.leadRoles - event.role
                    current.copy(leadRoles = if (event.leads && event.role in current.availableRoles) others + event.role else others)
                }
            }

            is SettingsEvent.SetLeadSkill -> {
                modifySettings { current ->
                    val others = current.leadSkills - event.skill
                    current.copy(leadSkills = if (event.leads && event.skill in current.workplaceSkills) others + event.skill else others)
                }
            }

            // --- AVAILABILITY SLOTS ---
            is SettingsEvent.AddAvailabilitySlot -> {
                modifySettings { current ->
                    val normalizedSlot = event.slot.copy(
                        startTime = event.slot.startTime.normalizeTime(),
                        endTime = event.slot.endTime.normalizeTime()
                    )
                    current.copy(definedAvailabilitySlots = current.definedAvailabilitySlots + normalizedSlot)
                }
            }

            is SettingsEvent.RemoveAvailabilitySlot -> {
                modifySettings { current ->
                    current.copy(
                        definedAvailabilitySlots = current.definedAvailabilitySlots.filter { it.id != event.slotId }
                    )
                }
            }

            // --- SALES TARGETS ---
            is SettingsEvent.UpdateSalesTarget -> {
                val currentFinancials = _uiState.value.workplaceFinancials ?: return
                val newTargets = currentFinancials.salesTargets.toMutableMap()
                newTargets[event.dayKey] = event.target
                updateFinancials(currentFinancials.copy(salesTargets = newTargets))
            }

            is SettingsEvent.UpdateSalesTargets -> {
                val currentFinancials = _uiState.value.workplaceFinancials ?: return
                updateFinancials(currentFinancials.copy(salesTargets = event.targets))
            }
        }
    }

    private fun removeWorkspace() {
        val currentWorkplace = _uiState.value.workplace ?: return
        if (_uiState.value.isSaving) return
        _uiState.update { it.copy(isSaving = true) }

        viewModelScope.launch {
            deleteWorkplaceUseCase(currentWorkplace.id).fold(
                onSuccess = {
                    if (sessionManager.currentWorkplaceId.value == currentWorkplace.id) {
                        sessionManager.setWorkplaceId(null)
                    }
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            isRemoved = true,
                            successMessage = UiText.Resource(Res.string.settings_workplace_removed),
                            workplace = null
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            error = UiText.Resource(Res.string.error_save_failed, e.toUiText())
                        )
                    }
                }
            )
        }
    }

    /**
     * Helper function to reduce boilerplate for settings updates.
     * Takes a lambda that receives the current settings and must return the new settings.
     */
    /** The ids of the owner's contract types the settings offer (the built-in contracts are SystemContractType names). */
    private fun customContractIds(): List<String> = _uiState.value.customContractTypes.map { it.id }

    private fun checkRequestType(
        current: com.evoapps.evoshift.domain.model.RequestSettings,
        name: String,
        checked: Boolean
    ): com.evoapps.evoshift.domain.model.RequestSettings {
        val rule = current.ruleOf(name)
        // Ticked with no contract: the recommended contracts, so it is offered to someone.
        val on = if (checked && !rule.hasAnyContract) com.evoapps.evoshift.domain.model.RequestTypeRule.defaultFor(name).copy(enabled = true) else rule.copy(enabled = checked)
        return current.copy(types = current.types + (name to on))
    }

    private fun updateTypesDraft(transform: (com.evoapps.evoshift.domain.model.RequestSettings) -> com.evoapps.evoshift.domain.model.RequestSettings) =
        _uiState.update { state -> state.copy(requestTypesDraft = state.requestTypesDraft?.let(transform)) }

    /** SH-263: the dialog's copy saved at once (optimistic, rolled back on failure), unless a ticked type has no contract. */
    private fun saveRequestTypes() {
        val draft = _uiState.value.requestTypesDraft ?: return
        if (draft.typesMissingContract().isNotEmpty()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.request_type_contracts_none)) }
            return
        }
        _uiState.update { it.copy(requestTypesDraft = null, requestTypeDraft = null) }
        modifyRequests { draft }
    }

    private fun openRequestType(name: String) {
        val requests = _uiState.value.requestTypesDraft ?: _uiState.value.workplace?.settings?.requests ?: return
        _uiState.update { it.copy(requestTypeDraft = RequestTypeDraft(name, requests.ruleOf(name), requests)) }
    }

    private fun updateDraft(transform: (RequestTypeDraft) -> RequestTypeDraft) =
        _uiState.update { state -> state.copy(requestTypeDraft = state.requestTypeDraft?.let { transform(it).copy(saveRefused = false) }) }

    private fun saveRequestType() {
        val draft = _uiState.value.requestTypeDraft ?: return
        if (draft.missingContract) {
            _uiState.update { it.copy(requestTypeDraft = draft.copy(saveRefused = true)) }
            return
        }
        val apply = { current: com.evoapps.evoshift.domain.model.RequestSettings ->
            com.evoapps.evoshift.domain.model.RequestLimit.of(draft.typeName)
                .fold(current) { acc, limit -> acc.with(limit, draft.numbers[limit]) }
                .let { it.copy(types = it.types + (draft.typeName to draft.rule)) }
        }
        _uiState.update { it.copy(requestTypeDraft = null) }
        // SH-263: inside the request types' dialog the type's settings change its copy only.
        if (_uiState.value.requestTypesDraft != null) updateTypesDraft(apply) else modifyRequests(apply)
    }

    /** The owner's contract types, live: the built-in ones (stored as their names) are not repeated. */
    private var contractTypesJob: kotlinx.coroutines.Job? = null

    private fun observeContractTypes(ownerId: String) {
        val repository = ownerRepository ?: return
        if (ownerId.isBlank() || contractTypesJob != null) return
        contractTypesJob = viewModelScope.launch {
            repository.observeContractTypes(ownerId).collect { types ->
                val custom = types
                    .filter { it.id.isNotBlank() && com.evoapps.evoshift.domain.model.SystemContractType.fromId(it.id) == com.evoapps.evoshift.domain.model.SystemContractType.NOT_SET }
                    .distinctBy { it.id }
                    .sortedBy { it.label.lowercase() }
                _uiState.update { it.copy(customContractTypes = custom) }
            }
        }
    }

    /** SH-223: the request settings, saved on their own (WorkplaceRepository.updateRequestSettings). */
    private fun modifyRequests(transform: (com.evoapps.evoshift.domain.model.RequestSettings) -> com.evoapps.evoshift.domain.model.RequestSettings) {
        val currentWorkplace = _uiState.value.workplace ?: return
        val requests = transform(currentWorkplace.settings.requests)
        if (requests == currentWorkplace.settings.requests) return
        _uiState.update { it.copy(workplace = currentWorkplace.copy(settings = currentWorkplace.settings.copy(requests = requests))) }
        viewModelScope.launch {
            saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id) + ":requests", online, queue = true) {
                if (groupId != null) {
                    val financials = _uiState.value.workplaceFinancials
                    saveGroup(com.evoapps.evoshift.domain.model.SettingsValues(currentWorkplace.settings, financials), com.evoapps.evoshift.domain.model.SettingsValues(currentWorkplace.settings.copy(requests = requests), financials))
                } else {
                    workplaceRepository.updateRequestSettings(currentWorkplace.id, requests)
                        .onSuccess { smartSync(currentWorkplace.settings, currentWorkplace.settings.copy(requests = requests)) }
                }
            }.fold(
                onSuccess = { _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.settings_saved)) } },
                onFailure = { e ->
                    _uiState.update { it.copy(workplace = currentWorkplace, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
                }
            )
        }
    }

    private fun modifySettings(transform: (WorkplaceSettings) -> WorkplaceSettings) {
        val currentWorkplace = _uiState.value.workplace ?: return

        // 1. Aplikujemy zmianę w pamięci
        val newSettings = transform(currentWorkplace.settings)

        // Jeśli nic się nie zmieniło (np. próba dodania zduplikowanej roli), przerywamy
        if (newSettings == currentWorkplace.settings) return

        val updatedWorkplace = currentWorkplace.copy(settings = newSettings)
        _uiState.update { it.copy(workplace = updatedWorkplace) }

        viewModelScope.launch {
            saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id), online, queue = true) { writeSettings(currentWorkplace.id, currentWorkplace.settings, newSettings) }.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(successMessage = it.successMessage ?: UiText.Resource(Res.string.settings_saved))
                    }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            workplace = currentWorkplace, // Wracamy do oryginału
                            error = UiText.Resource(Res.string.error_save_failed, e.toUiText())
                        )
                    }
                }
            )
        }
    }

    private fun updateName(newName: String) {
        val currentWorkplace = _uiState.value.workplace ?: return
        if (currentWorkplace.name == newName) return

        val updatedWorkplace = currentWorkplace.copy(name = newName)
        _uiState.update { it.copy(workplace = updatedWorkplace) }

        viewModelScope.launch {
            saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id), online, queue = true) { updateWorkplaceNameUseCase(currentWorkplace.id, newName) }.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(successMessage = UiText.Resource(Res.string.settings_name_updated))
                    }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            workplace = currentWorkplace, // Revert
                            error = UiText.Resource(Res.string.error_save_failed, e.toUiText())
                        )
                    }
                }
            )
        }
    }

    private fun updateBasicSettings(
        name: String,
        timezone: String,
        currency: String,
        firstDayOfWeek: Int
    ) {
        val currentWorkplace = _uiState.value.workplace ?: return
        val currentSettings = currentWorkplace.settings

        val newSettings = currentSettings.copy(
            timezone = timezone,
            currency = currency,
            firstDayOfWeek = firstDayOfWeek
        )

        val nameChanged = currentWorkplace.name != name
        val settingsChanged = currentSettings != newSettings

        if (!nameChanged && !settingsChanged) return

        _uiState.update { it.copy(workplace = currentWorkplace.copy(name = name, settings = newSettings)) }

        viewModelScope.launch {
            // SH-148: both writes in the app's saves, so leaving the screen cannot drop the second.
            var settingsError: Throwable? = null
            var nameError: Throwable? = null
            val outcome = saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id), online, queue = true) {
                // Update settings first if needed
                if (settingsChanged) {
                    writeSettings(currentWorkplace.id, currentSettings, newSettings).onFailure { settingsError = it }
                }
                // Update name if needed
                if (nameChanged && groupId == null) {
                    updateWorkplaceNameUseCase(currentWorkplace.id, name).onFailure { nameError = it }
                }
                (settingsError ?: nameError)?.let { Result.failure<Unit>(it) } ?: Result.success(Unit)
            }.onFailure { e -> if (settingsError == null && nameError == null) settingsError = e }

            // SH-186: kept on this device: the shell says so; the values on screen stay.
            if (outcome is SaveOutcome.KeptOnDevice) return@launch
            if (settingsError == null && nameError == null) {
                _uiState.update {
                    it.copy(successMessage = it.successMessage ?: UiText.Resource(Res.string.settings_updated))
                }
            } else {
                val failure = settingsError ?: nameError
                _uiState.update {
                    it.copy(
                        workplace = currentWorkplace,
                        error = failure?.let { e -> UiText.Resource(Res.string.error_save_failed, e.toUiText()) }
                    )
                }
                // One of the two writes may have landed: re-read (quietly) to show the DB truth.
                loadWorkplace(currentWorkplace.id)
            }
        }
    }

    /** SH-223: renames or removes a shared place, then reads the list again. */
    private fun changePlace(placeId: String, change: suspend (String) -> Result<Unit>?) {
        val workplaceId = _uiState.value.workplace?.id ?: return
        viewModelScope.launch {
            change(workplaceId)?.onFailure { e -> _uiState.update { it.copy(error = e.toUiText()) } }
            loadPlaces(workplaceId)
        }
    }

    private var footerPlanJob: kotlinx.coroutines.Job? = null

    /** SH-239: whether this workplace's plan lets the footer be hidden, kept up to date. */
    private fun observeFooterPlan(workplaceId: String) {
        val plans = observeWorkplacePlan ?: return
        footerPlanJob?.cancel()
        footerPlanJob = viewModelScope.launch {
            plans(workplaceId).collect { plan ->
                val canHide = com.evoapps.evoshift.domain.model.MadeWithFooter.canHide(plan, now())
                // SH-269: in group mode, the plan of the group's first workplace (the owner's plan).
                _uiState.update { if (it.workplace?.id == workplaceId || it.group != null) it.copy(footerCanBeHidden = canHide) else it }
            }
        }
    }

    private suspend fun loadPlaces(workplaceId: String) {
        val places = workPlaces?.list(workplaceId)?.getOrNull() ?: return
        _uiState.update { if (it.workplace?.id == workplaceId || it.workplace == null) it.copy(places = places) else it }
    }

    /** Shows the spinner only until this workplace is on screen; re-reads keep the content. */
    private fun loadWorkplace(workplaceId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.workplace?.id != workplaceId) }
            val capabilities = capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId).first()
            _uiState.update { it.copy(capabilities = capabilities) }
            val workplaceResult = getWorkplaceUseCase(workplaceId)
            // The financial settings only for those who may change them (nothing is asked otherwise).
            val financialsResult = if (capabilities.shows(Gate.WORKPLACE_FINANCES)) {
                workplaceRepository.getWorkplaceFinancials(workplaceId)
            } else {
                Result.success(null)
            }

            // SH-115: the labour budget is money too (an unreadable one just shows no section).
            val labourBudget = if (capabilities.shows(Gate.WORKPLACE_FINANCES)) getLabourBudget?.invoke(workplaceId)?.getOrNull() else null

            if (workplaceResult.isSuccess && financialsResult.isSuccess) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        workplace = workplaceResult.getOrNull(),
                        workplaceFinancials = financialsResult.getOrNull(),
                        labourBudget = labourBudget
                    )
                }
                if (capabilities.shows(Gate.SETTINGS_EDITING)) loadPlaces(workplaceId)
                workplaceResult.getOrNull()?.ownerId?.let { observeContractTypes(it) }
                observeFooterPlan(workplaceId)
                observeGroupLink(workplaceId)
            } else {
                val error = (workplaceResult.exceptionOrNull() ?: financialsResult.exceptionOrNull())?.toUiText()
                _uiState.update { it.copy(isLoading = false, error = error) }
            }
        }
    }

    private fun updatePolicy(newPolicy: SwapPolicy) {
        val currentWorkplace = _uiState.value.workplace ?: return

        // Kopiujemy settings i zmieniamy tylko politykę
        val newSettings = currentWorkplace.settings.copy(shiftSwapPolicy = newPolicy)
        _uiState.update { it.copy(workplace = currentWorkplace.copy(settings = newSettings)) }

        viewModelScope.launch {
            saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id), online, queue = true) { writeSettings(currentWorkplace.id, currentWorkplace.settings, newSettings) }.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(successMessage = it.successMessage ?: UiText.Resource(Res.string.settings_policy_updated))
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(workplace = currentWorkplace, error = e.toUiText()) }
                }
            )
        }
    }

    private fun addCostRule(name: String, amount: Double) {
        val currentWorkplace = _uiState.value.workplace ?: return

        viewModelScope.launch {
            val rule = RecurringCostRule(
                id = "", // Generowane w repo
                name = name,
                amount = amount,
                currency = currentWorkplace.settings.currency, // "PLN"
                frequency = CostFrequency.MONTHLY,
                targetWorkplaceId = currentWorkplace.id
            )

            // Zakładamy, że OwnerId == CurrentUser (w uproszczeniu bierzemy ownera z workplace)
            saves.runOrKeepOnDevice("workplace:${currentWorkplace.id}:cost-rules", online, queue = true) { createCostRuleUseCase(currentWorkplace.ownerId, rule) }.fold(
                onSuccess = {
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.cost_rule_added)) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(error = e.toUiText()) }
                }
            )
        }
    }

    private fun updateLocation(address: String, location: GeoLocation?) {
        val currentWorkplace = _uiState.value.workplace ?: return
        _uiState.update { it.copy(workplace = currentWorkplace.copy(address = address.trim(), location = location?.rounded())) }
        viewModelScope.launch {
            saves.runOrKeepOnDevice(settingsKey(currentWorkplace.id), online, queue = true) { updateWorkplaceLocationUseCase(currentWorkplace.id, address, location) }.fold(
                onSuccess = {
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.settings_location_updated)) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(workplace = currentWorkplace, error = e.toUiText()) }
                }
            )
        }
    }

    /** SH-115: optimistic: the section shows the new budget at once, a failed save puts it back. */
    private fun updateLabourBudget(budget: com.evoapps.evoshift.domain.budget.LabourBudget) {
        val save = saveLabourBudget ?: return
        val currentWorkplace = _uiState.value.workplace ?: return
        val before = _uiState.value.labourBudget
        _uiState.update { it.copy(labourBudget = budget.normalized()) }
        viewModelScope.launch {
            val by = getCurrentUserUseCase().first()?.uid.orEmpty()
            saves.runOrKeepOnDevice("workplace:${currentWorkplace.id}:labour-budget", online, queue = true) { save(currentWorkplace.id, budget, by) }.fold(
                onSuccess = { saved ->
                    _uiState.update { it.copy(labourBudget = saved, successMessage = UiText.Resource(Res.string.settings_labour_budget_saved)) }
                },
                onFailure = { e -> _uiState.update { it.copy(labourBudget = before, error = e.toUiText()) } }
            )
        }
    }

    // --- SH-269: group settings ---------------------------------------------------------------

    private var linkJob: kotlinx.coroutines.Job? = null

    /** The workplace's link to its group, kept up to date (null: in no group). */
    private fun observeGroupLink(workplaceId: String) {
        val repository = groupSettings ?: return
        linkJob?.cancel()
        linkJob = viewModelScope.launch {
            repository.observeLink(workplaceId).collect { link ->
                _uiState.update { if (it.workplace?.id == workplaceId) it.copy(groupLink = link) else it }
            }
        }
    }

    private fun currentValues(): com.evoapps.evoshift.domain.model.SettingsValues {
        val state = _uiState.value
        return com.evoapps.evoshift.domain.model.SettingsValues(state.workplace!!.settings, state.workplaceFinancials)
    }

    /** A settings save: the group's (group mode) or the workplace's, with smart sync. */
    private suspend fun writeSettings(workplaceId: String, before: WorkplaceSettings, after: WorkplaceSettings): Result<Unit> =
        if (groupId != null) {
            val financials = _uiState.value.workplaceFinancials
            saveGroup(
                com.evoapps.evoshift.domain.model.SettingsValues(before, financials),
                com.evoapps.evoshift.domain.model.SettingsValues(after, financials)
            )
        } else {
            updateSettingsUseCase(workplaceId, after).onSuccess { smartSync(before, after) }
        }

    private suspend fun writeFinancials(workplace: Workplace, before: WorkplaceFinancials?, after: WorkplaceFinancials): Result<Unit> =
        if (groupId != null) {
            saveGroup(
                com.evoapps.evoshift.domain.model.SettingsValues(workplace.settings, before),
                com.evoapps.evoshift.domain.model.SettingsValues(workplace.settings, after)
            )
        } else {
            updateWorkplaceFinancialsUseCase(workplace.id, after).onSuccess {
                smartSync(workplace.settings, workplace.settings, before, after)
            }
        }

    /** Group mode: the changed sections saved as the group's; the message says whom they reach. */
    private suspend fun saveGroup(
        before: com.evoapps.evoshift.domain.model.SettingsValues,
        after: com.evoapps.evoshift.domain.model.SettingsValues
    ): Result<Unit> {
        val target = group ?: return Result.success(Unit)
        val save = saveGroupSettings ?: return Result.failure(IllegalStateException("No group settings"))
        return save(target.groupId, before, after).map { summary ->
            if (summary.following.isNotEmpty() || summary.own.isNotEmpty()) {
                _uiState.update {
                    it.copy(
                        successMessage = groupSavedMessage(summary, target.workplaces),
                        groupFollowing = it.groupFollowing + summary.following,
                        groupOwn = it.groupOwn + summary.own
                    )
                }
            }
        }
    }

    /**
     * Smart sync after a workplace's own save: a section saved equal to the group's values follows
     * the group again; one that differs is the workplace's own (the server marks it so too).
     */
    private suspend fun smartSync(
        before: WorkplaceSettings,
        after: WorkplaceSettings,
        financialsBefore: WorkplaceFinancials? = null,
        financialsAfter: WorkplaceFinancials? = null
    ) {
        val link = _uiState.value.groupLink?.takeIf { it.isShared } ?: return
        val groupValues = link.group ?: return
        val old = com.evoapps.evoshift.domain.model.SettingsValues(before, financialsBefore)
        val new = com.evoapps.evoshift.domain.model.SettingsValues(after, financialsAfter)
        val changed = com.evoapps.evoshift.domain.model.GroupSettingsResolver.changed(old, new)
        if (changed.isEmpty()) return
        val toGroup = changed.filter {
            !link.follows(it) && com.evoapps.evoshift.domain.model.GroupSettingsResolver.sourceAfterSave(it, new, groupValues) ==
                com.evoapps.evoshift.domain.model.SectionSource.GROUP
        }
        val toOwn = changed.filter { link.follows(it) && !com.evoapps.evoshift.domain.model.GroupSettingsResolver.same(it, new, groupValues) }
        _uiState.update { state ->
            state.copy(groupLink = state.groupLink?.let { it.copy(inherited = it.inherited - toOwn.toSet() + toGroup.toSet()) })
        }
        val workplaceId = _uiState.value.workplace?.id ?: return
        if (toGroup.isNotEmpty()) setSectionInheritance?.invoke(workplaceId, toGroup.associateWith { true })
    }

    /** "Reset to group" / "Own settings": optimistic, put back when refused. */
    private fun setSectionSource(
        section: com.evoapps.evoshift.domain.model.SettingsSection,
        source: com.evoapps.evoshift.domain.model.SectionSource
    ) {
        val state = _uiState.value
        val workplace = state.workplace ?: return
        val link = state.groupLink ?: return
        val use = setSectionInheritance ?: return
        val follow = source == com.evoapps.evoshift.domain.model.SectionSource.GROUP
        if (link.follows(section) == follow) return
        val groupValues = link.group
        val copied = if (follow && groupValues != null) {
            com.evoapps.evoshift.domain.model.GroupSettingsResolver.copy(section, groupValues, currentValues())
        } else currentValues()
        _uiState.update {
            it.copy(
                workplace = workplace.copy(settings = copied.settings),
                workplaceFinancials = copied.financials ?: it.workplaceFinancials,
                groupLink = link.copy(inherited = if (follow) link.inherited + section else link.inherited - section)
            )
        }
        viewModelScope.launch {
            use(workplace.id, mapOf(section to follow)).fold(
                onSuccess = { inherited ->
                    _uiState.update {
                        it.copy(
                            groupLink = it.groupLink?.copy(inherited = inherited),
                            successMessage = UiText.Resource(if (follow) Res.string.group_settings_reset_done else Res.string.group_settings_own_done)
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(workplace = workplace, workplaceFinancials = state.workplaceFinancials, groupLink = link, error = e.toUiText())
                    }
                }
            )
        }
    }

    /** Group mode: a workplace's templates become the group's. */
    private fun useTemplatesFrom(workplaceId: String) {
        val target = group ?: return
        val save = saveGroupSettings ?: return
        if (workplaceId !in target.workplaces) return
        viewModelScope.launch {
            val values = currentValues()
            save(target.groupId, values, values, templatesFrom = workplaceId).fold(
                onSuccess = { summary -> _uiState.update { it.copy(successMessage = groupSavedMessage(summary, target.workplaces)) } },
                onFailure = { e -> _uiState.update { it.copy(error = e.toUiText()) } }
            )
        }
    }

    /**
     * Group mode: the group's values on screen as if they were a workplace's (the same sections and
     * dialogs). A group without values yet starts from its first workplace's, as the server
     * initialises it on the first save.
     */
    private fun loadGroup(groupId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val user = getCurrentUserUseCase().first()
            val found = user?.uid?.let { uid -> getGroups?.invoke(uid)?.getOrNull()?.firstOrNull { it.id == groupId } }
            val repository = groupSettings
            if (found == null || repository == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_not_found)) }
                return@launch
            }
            val names = found.workplaceIds.associateWith { id -> getWorkplaceUseCase(id).getOrNull()?.name ?: id }
            val target = GroupSettingsTarget(groupId, found.name, found.usesDefaultName, names)
            val first = found.workplaceIds.firstOrNull()
            val capabilities = first?.let { capabilitiesAt(getCurrentUserUseCase(), observePlanActive, it).first() }.orEmpty()
            val settings = repository.getGroupSettings(groupId, found.workplaceIds)
            val values = settings.getOrNull()?.values ?: first?.let { id ->
                getWorkplaceUseCase(id).getOrNull()?.let { wp ->
                    com.evoapps.evoshift.domain.model.SettingsValues(wp.settings, workplaceRepository.getWorkplaceFinancials(id).getOrNull())
                }
            }
            if (values == null) {
                _uiState.update {
                    it.copy(isLoading = false, group = target, error = settings.exceptionOrNull()?.toUiText() ?: UiText.Resource(Res.string.error_not_found))
                }
                return@launch
            }
            _uiState.update {
                it.copy(
                    isLoading = false,
                    group = target,
                    capabilities = capabilities,
                    workplace = Workplace(
                        id = groupId, ownerId = found.ownerId, name = found.name, address = "", location = null,
                        settings = values.settings, groupId = groupId
                    ),
                    workplaceFinancials = values.financials ?: WorkplaceFinancials(),
                    groupFollowing = settings.getOrNull()?.following.orEmpty(),
                    groupOwn = settings.getOrNull()?.own.orEmpty()
                )
            }
            if (first != null) observeFooterPlan(first)
            observeContractTypes(found.ownerId)
        }
    }

    private fun updateFinancials(financials: WorkplaceFinancials) {
        val currentWorkplace = _uiState.value.workplace ?: return
        val before = _uiState.value.workplaceFinancials
        _uiState.update { it.copy(workplaceFinancials = financials) }
        viewModelScope.launch {
            saves.runOrKeepOnDevice("workplace:${currentWorkplace.id}:financials", online, queue = true) { writeFinancials(currentWorkplace, before, financials) }.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(successMessage = it.successMessage ?: UiText.Resource(Res.string.settings_financials_updated))
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(workplaceFinancials = before, error = e.toUiText()) }
                }
            )
        }
    }
}

/** SH-269: "Applies to N workplaces; X keeps its own" after a group save ([names]: workplace id to name). */
internal fun groupSavedMessage(summary: com.evoapps.evoshift.domain.model.GroupSaveSummary, names: Map<String, String>): UiText {
    val keepOwn = summary.keepOwn.map { names[it] ?: it }.sorted()
    val applies = summary.appliesTo.size
    return if (keepOwn.isEmpty()) {
        UiText.Resource(Res.string.group_settings_saved_all, applies)
    } else {
        UiText.Resource(Res.string.group_settings_saved_some_own, applies, keepOwn.joinToString(", "))
    }
}

/** SH-148: a workplace's settings and name in [com.evoapps.evoshift.saves.PendingSaves]: written one after another. */
internal fun settingsKey(workplaceId: String) = "workplace:$workplaceId:settings"
